import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'


import { InternalPages } from '@/enums/allowedPages'
import { isAuthenticated } from '@/libs/auth'
import i18n from '@/libs/i18n'
import { parseError } from '@/libs/error'
import { PrintError } from '@/libs/toast'
import { useBulletinStore } from '@/stores/bulletin'
import { useFetchStore } from '@/stores/fetch'
import { useNotificationStore } from '@/stores/notification'
import type { UserManager } from '@/types/Manager.ts'
import type { Unit } from '@/types/Unit'
import type { UserData } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const t = i18n.global.t

    const windowWidth: Ref<number> = ref(0)
    const loaderActive: Ref<number> = ref(0)
    const mobileMenuOpen: Ref<boolean> = ref(false)
    const selectedUnit: Ref<Unit | undefined> = ref(undefined)
    const selectedManager: Ref<UserManager | undefined> = ref(undefined)
    const userUnits: Ref<Unit[]> = ref([])
    const rawUserUnits: Ref<Unit[]> = ref([])
    const hasUserTenants: Ref<boolean> = ref(false)
    const allowedPages: Ref<number[]> = ref([])
    const userData: Ref<UserData> = ref({
        id: '',
        firstname: '',
        lastname: '',
        email: '',
        selected_manager: '',
        code_payer: ''
    })
    const internalPages = [
        InternalPages.Login, InternalPages.Register, InternalPages.ForgottenPassword,
        InternalPages.ResetPassword, InternalPages.AddManager, InternalPages.LoginAsUser,
        InternalPages.Home, InternalPages.Profil, InternalPages.Forms
    ]

    const resizeHandler = (): void => {
        windowWidth.value = window.innerWidth
    }

    const isMobile = (): boolean => {
        return windowWidth.value < 1024
    }

    const isMediumScreen = (): boolean => {
        return windowWidth.value < 768
    }

    const startLoading = () => {
        loaderActive.value++
    }
    const stopLoading = () => {
        if (loaderActive.value > 0) loaderActive.value--
    }

    const setLogin = (data: UserData, jwt: string) => {
        localStorage.setItem('userData', JSON.stringify(data))
        localStorage.setItem('JWT', jwt)
        userData.value = data
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        localStorage.removeItem('userData')
        localStorage.removeItem('selectedUnit')
        selectedUnit.value = undefined
        selectedManager.value = undefined
        userData.value = {
            id: '',
            firstname: '',
            lastname: '',
            email: '',
            selected_manager: '',
            code_payer: ''
        }
        allowedPages.value = internalPages
        localStorage.setItem('allowedPages', JSON.stringify(internalPages))
        useNotificationStore().setUnreadNotifications(0)
        useBulletinStore().setUnreadBulletins(0)
    }

    const setInternalPages = () => {
        allowedPages.value = internalPages
        localStorage.setItem('allowedPages', JSON.stringify(internalPages))
    }

    const setUserData = (data: UserData) => {
        userData.value = data
    }

    const setSelectedUnit = (unit: Unit | undefined) => {
        selectedUnit.value = unit
        if (unit) localStorage.setItem('selectedUnit', JSON.stringify(unit))
        else localStorage.removeItem('selectedUnit')

        useNotificationStore().updateUnreadNotifications()
        useBulletinStore().updateUnreadBulletins()
    }

    const setSelectedManager = (manager: UserManager | undefined) => {
        selectedManager.value = manager
    }

    const setUnits = (units: Unit[]) => {
        userUnits.value = units
    }

    const setRawUnits = (units: Unit[]) => {
        rawUserUnits.value = units
    }

    const setAllowedPagesAsync = async() => {
        try {
            if (!isAuthenticated()) {
                allowedPages.value = internalPages
                localStorage.setItem('allowedPages', JSON.stringify(internalPages))
                return
            }

            const fetchStore = useFetchStore()
            const [allowedPagesResult, hasUserTenantsResult] = await Promise.all([
                fetchStore.fetch('/api/iiportal-user/v2/allowed_pages').get().json<number[]>(),
                fetchStore.fetch('/api/iiportal-user/v2/has_user_tenants').get().json<boolean>()
            ])

            if (allowedPagesResult.data.value) {
                allowedPages.value = [...allowedPagesResult.data.value, ...internalPages]
                localStorage.setItem('allowedPages', JSON.stringify(allowedPages.value))
            } else {
                PrintError(parseError(allowedPagesResult.error.value, t('errors.load_data')))
            }

            if (hasUserTenantsResult.data.value !== null) hasUserTenants.value = hasUserTenantsResult.data.value
        } catch(error) {
            PrintError(parseError(error, t('errors.load_data')))
        }
    }

    const setAllowedPages = () => {
        if (!isAuthenticated()) {
            allowedPages.value = internalPages
            localStorage.setItem('allowedPages', JSON.stringify(internalPages))
            return
        }

        const fetchStore = useFetchStore()
        void fetchStore.fetch('/api/iiportal-user/v2/allowed_pages').get().json<number[]>().then(({ data }) => {
            if (!data.value) return
            allowedPages.value = [...data.value, ...internalPages]
            localStorage.setItem('allowedPages', JSON.stringify(allowedPages.value))
        })
    }

    // getters
    const isLoaderActive = () => {
        return !!loaderActive.value
    }

    const getUserData = () => {
        return userData.value
    }

    const getUserLoggedIn = () => {
        return isAuthenticated()
    }

    const getSelectedUnit = (): Unit | undefined => {
        if (selectedUnit.value) {
            if (typeof selectedUnit.value === 'object') return selectedUnit.value
            else return JSON.parse(selectedUnit.value)
        }
        return undefined
    }

    const getUnits = () => {
        return userUnits.value
    }

    const ifUserHasTenants = () => {
        return hasUserTenants.value
    }

    const getAllowedPages = () => {
        return allowedPages.value
    }

    return {
        windowWidth,
        mobileMenuOpen,
        selectedManager,
        isMobile,
        isMediumScreen,
        resizeHandler,
        setLogin,
        setLogout,
        isLoaderActive,
        startLoading,
        stopLoading,
        setUserData,
        setSelectedUnit,
        setUnits,
        setRawUnits,
        setAllowedPagesAsync,
        setAllowedPages,
        getUserData,
        getUserLoggedIn,
        getSelectedUnit,
        getUnits,
        ifUserHasTenants,
        getAllowedPages,
        setInternalPages,
        setSelectedManager
    }
})
